import { FC, useState } from 'react';
import { Button, Flex, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined
} from '@ant-design/icons';
import axios from 'axios';

import { instance } from 'utils/api';
import { downloadFileFromUrl } from 'utils/file';

interface IProps {
  patientId: string;
  name: string;
  format: string;
  size: number;
}

const FileItem: FC<IProps> = ({ name, format, size, patientId }) => {
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();

  const downloadFile = async () => {
    try {
      setLoading(true);
      const res = await instance.get(
        `storage/file/url/${name}?user_id=${patientId}`
      );

      const { url } = res.data.data;

      downloadFileFromUrl(url, name);
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.data) {
        message.error(error.response.data.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderIcon = () => {
    const style = { color: '#ffffff', fontSize: 16 };

    switch (format?.split('/')?.[1]?.toLowerCase()) {
      case 'jpeg':
      case 'jpg':
      case 'png':
        return <FileImageOutlined style={style} />;
      case 'dicom':
      case 'dcm':
        return <FileSearchOutlined style={style} />;
      case 'pdf':
        return <FilePdfOutlined style={style} />;
      case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
      case 'msword':
        return <FileWordOutlined style={style} />;
      case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'vnd.ms-excel':
      case 'csv':
        return <FileExcelOutlined style={style} />;
      case 'x-7z-compressed':
      case 'zip':
        return <FileZipOutlined style={style} />;
      default:
        return <FileTextOutlined style={style} />;
    }
  };

  return (
    <>
      <Flex
        align="center"
        gap={16}
        style={{ flex: '1 0 50%', maxWidth: '50%' }}
      >
        <span className="list-item-bg">{renderIcon()}</span>
        <span className="list-content-item-value">{name}</span>
      </Flex>
      <p>
        <span className="list-content-item-value">{size} MB</span>
      </p>
      <div style={{ width: 165, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="large"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={downloadFile}
          loading={isLoading}
          style={{ width: '100%' }}
          ghost
        >
          {t('common.download')}
        </Button>
      </div>
    </>
  );
};

export default FileItem;
